import { useQuery } from '@tanstack/react-query'
import * as R from 'remeda'

import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { applyIfExist, exists, filterExists } from '~/utils'

import { useBrandAccountSurveyGroupsQueryKey } from './useBrandAccountSurveyGroupsQueryKey'

const document = graphql(`
  query UseBrandAccountSurveyGroupsQuery_Main($brandAccountId: ID!) {
    brandAccount(brandAccountId: $brandAccountId) {
      id
      surveyGroups {
        id
        brands {
          id
        }
        questions {
          id
        }
        surveys {
          id
          numberOfTotalAnswers
        }
      }
    }
  }
`)

export const useBrandAccountSurveyGroupsQuery = (
  brandAccountId: string | undefined,
) => {
  const queryKey = useBrandAccountSurveyGroupsQueryKey(brandAccountId)
  const graphQLRequest = useGraphQLRequest()

  return useQuery({
    queryFn: async () => {
      if (!exists(brandAccountId)) {
        return null
      }
      const result = await graphQLRequest({
        document,
        variables: { brandAccountId },
      })

      return R.pipe(
        result.brandAccount,
        applyIfExist((brandAccount) =>
          R.pipe(
            brandAccount.surveyGroups,
            filterExists,
            R.map((surveyGroup) => ({
              ...surveyGroup,
              brands: R.pipe(surveyGroup.brands, filterExists),
              questions: R.pipe(surveyGroup.questions, filterExists),
              surveys: R.pipe(
                surveyGroup.surveys,
                filterExists,
                R.map((survey) => ({
                  ...survey,
                  numberOfTotalAnswers:
                    survey.numberOfTotalAnswers ?? undefined,
                })),
              ),
            })),
          ),
        ),
      )
    },
    queryKey,
  })
}

export type BrandAccountSurveyGroupsQueryData = QueryResultOf<
  typeof useBrandAccountSurveyGroupsQuery
>

export type SurveyGroup = NonNullable<BrandAccountSurveyGroupsQueryData>[number]
